import {
  CheckCircle,
  Clock,
  UploadCloud,
  Loader2,
  RefreshCw,
  XCircle,
} from "lucide-react";
import type { MCAPSuspensionPayload } from "../../hooks/useCopilotRuntime";
import type { OverallState } from "./mcapUploadState";

interface McapUploadLiveViewProps {
  payload: MCAPSuspensionPayload;
  overall: OverallState;
  /** Persisted workflow node of the active thread (analysis phase hints). */
  workflowNode: string | undefined;
  submitting: boolean;
  onRetry: () => void;
}

/**
 * Live extraction feedback — replaces the approval prompt in place once the
 * extraction has been approved (in this session or server-side).
 */
export function McapUploadLiveView({
  payload,
  overall,
  submitting,
  onRetry,
}: McapUploadLiveViewProps) {
  if (overall.kind === "UPLOADED") {
    return (
      <div className="my-2 flex items-center gap-2 text-sm text-neutral-300">
        <CheckCircle className="h-4 w-4 text-emerald-400 shrink-0" />
        <span>
          Extraction complete - {overall.total} segment
          {overall.total > 1 ? "s" : ""} uploaded.
        </span>
      </div>
    );
  }

  return (
    <div className="my-3 rounded-lg border border-neutral-700/50 bg-neutral-900/40 overflow-hidden">
      <div className="flex items-center gap-2 border-b border-neutral-700/40 bg-neutral-800/40 px-4 py-2.5">
        <UploadCloud className="h-4 w-4 text-sky-400 shrink-0" />
        <span className="text-xs font-semibold uppercase tracking-wider text-neutral-300">
          MCAP Log Extraction
        </span>
        <span className="ml-auto font-mono text-[10px] text-neutral-500 truncate">
          {payload.device_name || "Selected device"}
        </span>
      </div>

      <div className="px-4 py-3 space-y-3">
        {overall.kind === "QUEUED_OFFLINE" && (
          <div className="flex items-start gap-2 rounded-md border border-amber-600/40 bg-amber-950/20 px-3 py-2.5">
            <Clock className="mt-0.5 h-4 w-4 text-amber-400 shrink-0" />
            <div className="text-sm text-amber-200">
              <p className="font-medium">Queued — device offline</p>
              <p className="text-xs text-amber-300/80 mt-0.5">
                {overall.reason ??
                  "The extraction will start automatically when the device reconnects."}
              </p>
            </div>
          </div>
        )}

        {overall.kind === "PENDING" && (
          <div className="flex items-center gap-2 rounded-md border border-neutral-700/60 px-3 py-2.5">
            <Loader2 className="h-4 w-4 animate-spin text-sky-400 shrink-0" />
            <span className="text-sm text-neutral-300">
              Dispatched — waiting for the device to accept the extraction…
            </span>
          </div>
        )}

        {overall.kind === "RETRYING" && (
          <div className="flex items-center gap-2 rounded-md border border-amber-600/40 bg-amber-950/20 px-3 py-2.5">
            <RefreshCw className="h-4 w-4 animate-spin text-amber-400 shrink-0" />
            <span className="text-sm text-amber-200">
              Transfer interrupted — retrying…
            </span>
          </div>
        )}

        {(overall.kind === "UPLOADING" || overall.kind === "RETRYING") && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-400">
                Uploading {overall.done}/{overall.total} segment
                {overall.total > 1 ? "s" : ""}
              </span>
              <span className="font-semibold text-neutral-200">
                {overall.progress}%
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-800">
              <div
                className="h-full rounded-full bg-sky-500 transition-all duration-500"
                style={{ width: `${overall.progress}%` }}
              />
            </div>
          </div>
        )}

        {overall.kind === "FAILED" && (
          <div className="space-y-2">
            <div className="flex items-start gap-2 rounded-md border border-red-600/40 bg-red-950/30 px-3 py-2.5">
              <XCircle className="mt-0.5 h-4 w-4 text-red-400 shrink-0" />
              <div className="text-sm text-red-200">
                <p className="font-medium">Extraction failed</p>
                <p className="text-xs text-red-300/80 mt-0.5">
                  {overall.reason ??
                    "The edge device could not complete the transfer."}
                </p>
              </div>
            </div>
            <button
              onClick={onRetry}
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-neutral-600 px-4 py-2 text-sm text-neutral-300 transition-colors hover:bg-neutral-700 disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Retry extraction
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
